import {
  BtnLanguage,
  LanguageBox,
  LanguageListBox,
  PolicyArticleTitle,
  PolicyContainer,
  PolicyHyphenList,
  PolicyNumberList,
  PolicySectionTitle,
  PolicyText,
  PolicyTitle,
  PolicyVersion,
  StyledIconDropdown,
  StyledIconGlobal,
} from "styles/PolicyCommon";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const ServiceEn = () => {
  const [isOpen, setIsOpen] = useState(false);
  const onLanguageClick = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    document.documentElement.lang = "en";
    document.title = "pregnancy&baby";
    return () => {
      document.documentElement.lang = "en";
      document.title = "pregnancy&baby";
    };
  }, []);

  return (
    <PolicyContainer>
      <h1 className="hide">pregnancy&amp;baby</h1>
      <LanguageBox isOpen={isOpen}>
        <BtnLanguage onClick={onLanguageClick}>
          <StyledIconGlobal />
          EN
          <StyledIconDropdown />
        </BtnLanguage>
        <LanguageListBox>
          <ul>
            <li className="selected">
              <em>EN</em>
            </li>
            <li>
              <Link to="/policy/ja/service">JA</Link>
            </li>
            <li>
              <Link to="/policy/vi/service">VI</Link>
            </li>
            <li>
              <Link to="/policy/th/service">TH</Link>
            </li>
            <li>
              <Link to="/policy/id/service">ID</Link>
            </li>
            <li>
              <Link to="/policy/es/service">ES</Link>
            </li>
          </ul>
        </LanguageListBox>
      </LanguageBox>

      <PolicyTitle>Terms of Use</PolicyTitle>

      <PolicyVersion>
        Effective Date: [September 21, 2026]
        <br />
        Last Updated: [August 25, 2026]
      </PolicyVersion>

      <PolicySectionTitle>
        Chapter 1. Welcome to pregnancy&amp;baby
      </PolicySectionTitle>
      <PolicyText>
        <strong>pregnancy&amp;baby</strong> provides a variety of services
        designed to support users throughout pregnancy preparation, fertility
        treatment, pregnancy, childbirth, postpartum recovery, and child care.
        These services may include health and diary tracking, personalized
        reports, informational content, community features, and other related
        functions.
      </PolicyText>
      <PolicyText>
        These Terms of Use (“Terms”) govern your use of the{" "}
        <strong>pregnancy&amp;baby</strong> mobile application, website, and
        related services (collectively, the “Services”) provided by Connecti
        Co., Ltd. (the “Company,” “we,” “us,” or “our”).
      </PolicyText>
      <PolicyText>
        Please read these Terms carefully before using the Services.
      </PolicyText>

      <PolicyArticleTitle>
        Article 1. Purpose and Definitions
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          These Terms set forth the rights, obligations, responsibilities,
          conditions of use, and other matters relating to the use of the
          Services between the Company and users who agree to these Terms and
          complete account registration (“Members”).
        </li>
        <li>
          For purposes of these Terms:
          <PolicyHyphenList>
            <li>
              <strong>“Services”</strong> means the pregnancy&amp;baby mobile
              application, website, and any related services provided by the
              Company.
            </li>
            <li>
              <strong>“Member”</strong> means a user who agrees to these Terms
              and completes registration for the Services.
            </li>
            <li>
              <strong>“Account”</strong> means the account used by a Member to
              access and use the Services.
            </li>
            <li>
              <strong>“Account</strong> Information” means information
              associated with a Member's Account that may be viewed or managed
              through the Services.
            </li>
            <li>
              <strong>“Content”</strong> means text, images, photographs,
              videos, data, information, comments, posts, and other materials
              available through or submitted to the Services.
            </li>
            <li>
              <strong>“Paid Services”</strong> means digital content, features,
              reports, or other services provided by the Company that are
              available through a <strong>one-time payment.</strong>
            </li>
          </PolicyHyphenList>
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 2. Effectiveness and Modification of the Terms
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          These Terms become effective when they are made available within the
          Services or by other appropriate means and a Member agrees to them.
        </li>
        <li>
          The Company may amend these Terms to the extent permitted by
          applicable law.
        </li>
        <li>
          Where these Terms are amended, the Company will generally notify
          Members of the changes and the effective date at least{" "}
          <strong>15 days before</strong> the amended Terms take effect.
        </li>
        <li>
          If an amendment materially disadvantages Members, the Company will
          generally provide notice at least <strong>30 days before</strong> the
          effective date by email, in-app notice, push notification, or another
          appropriate electronic method.
        </li>
        <li>
          Where permitted by applicable law, if the Company clearly informs
          Members that failure to object within a specified period will
          constitute acceptance of the amended Terms and a Member does not
          object within that period, the Member may be deemed to have accepted
          the amended Terms.
        </li>
        <li>
          If a Member does not agree to the amended Terms, the Member may
          terminate the service agreement by deleting their Account.
        </li>
        <li>
          These Terms generally apply from the date a Member agrees to them
          until termination of the service agreement. Provisions that by their
          nature are intended to survive termination may remain effective after
          termination.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>Article 3. Additional Rules</PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Matters not expressly provided for in these Terms will be governed by
          applicable laws, individual service terms, and the Company's operating
          policies, guidelines, and rules (“Operating Policies”).
        </li>
        <li>
          Where a specific service is subject to additional terms, those
          additional terms will apply to that service.
        </li>
        <li>
          If these Terms conflict with mandatory provisions of applicable law,
          the applicable law will prevail.
        </li>
      </PolicyNumberList>

      <PolicySectionTitle>
        Chapter 2. Membership and Account Management
      </PolicySectionTitle>
      <PolicyArticleTitle>Article 4. Registration</PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          A user is deemed to have agreed to these Terms by selecting an
          “Agree,” “Accept,” “Continue,” or equivalent button or checkbox
          presented during registration.
        </li>
        <li>
          To register for the Services, a user must provide the information
          required by the registration process and complete the registration
          procedure designated by the Company.
        </li>
        <li>
          Members may create or access an Account through methods supported by
          the Company, including:
          <PolicyHyphenList>
            <li>Email</li>
            <li>Google</li>
            <li>Apple</li>
          </PolicyHyphenList>
        </li>
        <li>
          The Company may request additional information or verification where
          reasonably necessary to provide a particular feature, protect Members,
          comply with applicable law, or prevent misuse of the Services.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 5. Acceptance, Suspension, and Refusal of Registration
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Registration is completed when the user submits a registration request
          and the Company accepts it.
        </li>
        <li>
          The Company will generally accept a properly completed registration
          request. However, the Company may suspend, refuse, or cancel
          registration if:
          <PolicyHyphenList>
            <li>
              The applicant uses another person's identity, email address,
              account, or other information without authorization;
            </li>
            <li>
              The applicant provides false, incomplete, or misleading
              information;
            </li>
            <li>
              The Company lacks reasonable technical capacity to provide the
              Services;
            </li>
            <li>
              Technical or operational issues prevent the provision of the
              Services;
            </li>
            <li>
              A person whose use of the Services was previously suspended or
              restricted attempts to avoid such restriction by creating another
              Account;
            </li>
            <li>
              Registration would violate applicable laws, these Terms, or the
              Operating Policies; or
            </li>
            <li>
              The Company reasonably determines that registration cannot be
              accepted for legitimate technical, security, operational, or legal
              reasons.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          If the Company later discovers that a Member registered in violation
          of this Article, the Company may restrict use of the Services, suspend
          the Account, or delete the Account as appropriate.
        </li>
      </PolicyNumberList>
      <PolicyArticleTitle>Article 6. Account Management</PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Members are responsible for appropriately managing their Accounts and
          preventing unauthorized use by third parties.
        </li>
        <li>
          If a Member becomes aware of unauthorized access to or use of their
          Account, the Member should promptly take reasonable steps to secure
          the Account and notify the Company.
        </li>
        <li>
          Members may view or modify certain Account Information through the
          Services. Certain information may not be directly editable where
          restrictions are necessary for account security, identification, or
          technical reasons.
        </li>
        <li>
          If a Member needs to update information that cannot be modified
          directly, the Member may contact Customer Support.
        </li>
        <li>
          When a Member uses Google, Apple login, authentication is performed
          using a token or other authentication mechanism provided by the
          relevant third-party service. The Company does not provide password
          recovery for login credentials managed by those third-party providers.
        </li>
      </PolicyNumberList>

      <PolicySectionTitle>Chapter 3. Use of the Services</PolicySectionTitle>
      <PolicyArticleTitle>Article 7. Provision of Services</PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Members may use the Services after completing registration and any
          procedures required for the relevant feature.
        </li>
        <li>
          The Services may include, among other things:
          <PolicyHyphenList>
            <li>Pregnancy preparation and menstrual cycle tracking</li>
            <li>Ovulation and pregnancy test tracking and analysis</li>
            <li>Fertility treatment tracking</li>
            <li>Pregnancy diaries and pregnancy-related reports</li>
            <li>Fetal growth reports</li>
            <li>Pregnancy and postpartum weight management</li>
            <li>Child growth and developmental features</li>
            <li>Child health and care tracking</li>
            <li>Informational guides and content</li>
            <li>Community features</li>
            <li>Other features made available by the Company</li>
          </PolicyHyphenList>
        </li>
        <li>
          Certain features may require:
          <PolicyHyphenList>
            <li>Additional information;</li>
            <li>
              Separate consent, including consent relating to health information
              or other sensitive Personal Data;
            </li>
            <li>Age or eligibility requirements;</li>
            <li>Additional terms or policies; or</li>
            <li>
              Other reasonable requirements necessary to provide the feature.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          The Company may provide software updates to improve or modify the
          Services or to add or remove features. Members are encouraged to use
          the latest available version of the application.
        </li>
        <li>
          The Company may display service notices, administrative messages,
          informational content, or other communications within the Services.
        </li>
        <li>
          Commercial or promotional emails, push notifications, or similar
          communications will be sent in accordance with the Member's consent
          and applicable law.
        </li>
        <li>
          If a Member discovers a system error or other issue while using the
          Services, the Member may contact Customer Support at:
          <br />
          <a href="mailto:after10month@connect-i.co.kr">
            after10month@connect-i.co.kr
          </a>
        </li>
        <li>
          Members are responsible for any data or telecommunications charges
          imposed by their internet or mobile service provider in connection
          with their use of the Services.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 8. Suspension, Modification, and Termination of Services
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          The Company will use reasonable efforts to provide the Services
          continuously and reliably.
        </li>
        <li>
          Unless otherwise required by law, all or part of the Services may be
          temporarily restricted, suspended, modified, or discontinued in
          circumstances including:
          <PolicyHyphenList>
            <li>
              Maintenance, inspection, repair, or replacement of systems or
              equipment;
            </li>
            <li>
              Power outages, equipment failure, network congestion, or other
              technical issues;
            </li>
            <li>
              Termination or modification of agreements with third-party service
              providers;
            </li>
            <li>Government orders, regulations, or legal requirements;</li>
            <li>Changes in operational, security, or service policies;</li>
            <li>
              Natural disasters, emergencies, war, civil disturbance, or other
              force majeure events; or
            </li>
            <li>
              Other circumstances that make continued provision of the Services
              impracticable.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          Where reasonably possible, the Company will provide prior notice of a
          material suspension or termination through the methods described in
          Article 21.
        </li>
        <li>
          Prior notice may not be possible where the suspension results from
          unexpected or uncontrollable circumstances, such as server failure,
          system outage, security incidents, or failures of third-party systems.
        </li>
        <li>
          In such circumstances, the Company will make reasonable efforts to
          restore the Services as promptly as practicable.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 9. Management of Member Content
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          If Content posted by a Member infringes copyright, privacy,
          intellectual property, or other rights, a rights holder may request
          appropriate action in accordance with applicable law.
        </li>
        <li>
          The Company may restrict access to, temporarily remove, or permanently
          remove Content where the Company reasonably determines that the
          Content:
          <PolicyHyphenList>
            <li>Violates applicable law;</li>
            <li>Infringes the rights of another person;</li>
            <li>Violates these Terms or the Operating Policies;</li>
            <li>
              Threatens the security or proper operation of the Services; or
            </li>
            <li>
              Otherwise requires action to protect Members or third parties.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          Procedures relating to reports, takedowns, and other Content-related
          actions may be further specified in the Operating Policies.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 10. Ownership and Use of Content
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Members may submit photographs, text, information, videos, comments,
          suggestions, and other Content to the Services.
        </li>
        <li>
          Members retain ownership of the intellectual property rights they hold
          in Content they submit.
        </li>
        <li>
          By submitting Content to areas of the Services intended to be shared
          with others, the Member grants the Company a non-exclusive license to
          host, store, reproduce, display, format, and technically modify such
          Content to the extent reasonably necessary to operate, display,
          provide, and improve the relevant feature.
        </li>
        <li>
          Member Content may appear in search results, feeds, personalized
          content areas, or other locations within the Services where
          appropriate to the feature in which it was submitted.
        </li>
        <li>
          The Company may refuse, restrict, or remove Content without prior
          notice where it reasonably determines that the Content:
          <PolicyHyphenList>
            <li>Is unlawful;</li>
            <li>Is obscene or sexually exploitative;</li>
            <li>Promotes hatred, unlawful discrimination, or violence;</li>
            <li>Constitutes spam or unauthorized advertising;</li>
            <li>Impersonates another person;</li>
            <li>Infringes third-party rights;</li>
            <li>Is fraudulent or misleading; or</li>
            <li>Otherwise violates these Terms or the Operating Policies.</li>
          </PolicyHyphenList>
        </li>
        <li>
          The Company is not obligated to proactively review all Member Content.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>Article 11. Restrictions on Use</PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Members must not engage in any of the following activities:
          <PolicyHyphenList>
            <li>
              Providing false information or improperly using another person's
              Account or identity;
            </li>
            <li>
              Copying, modifying, distributing, selling, transferring, lending,
              reverse engineering, decompiling, or attempting to extract source
              code from the Services without authorization, except where
              applicable law expressly permits such conduct;
            </li>
            <li>
              Defaming, threatening, harassing, or otherwise harming another
              person;
            </li>
            <li>
              Posting unlawful, obscene, exploitative, or otherwise prohibited
              material;
            </li>
            <li>
              Infringing copyrights, trademarks, privacy rights, or other rights
              of the Company or a third party;
            </li>
            <li>
              Publishing false or misleading information concerning the Company
              or another person;
            </li>
            <li>
              Distributing Content contrary to applicable law or public order;
            </li>
            <li>
              Introducing malware, viruses, malicious code, or other material
              designed to damage or disrupt the Services;
            </li>
            <li>
              Intentionally interfering with or disrupting the normal operation
              of the Services;
            </li>
            <li>
              Impersonating another person or falsely representing an
              affiliation with another person or organization;
            </li>
            <li>
              Collecting, storing, or publicly disclosing another Member's
              Personal Data without proper authorization;
            </li>
            <li>
              Distributing fraudulent information for financial gain or to cause
              damage;
            </li>
            <li>Engaging in unlawful gambling or other illegal activities;</li>
            <li>
              Soliciting or facilitating unlawful sexual services or
              exploitative conduct;
            </li>
            <li>
              Repeatedly sending Content intended to cause fear, humiliation,
              harassment, or substantial distress;
            </li>
            <li>
              Transmitting or publishing information prohibited by applicable
              law;
            </li>
            <li>
              Impersonating Company staff, service administrators, or business
              partners;
            </li>
            <li>
              Abusing reporting systems through false or malicious reports;
            </li>
            <li>
              Stalking, engaging in abusive conduct, or otherwise interfering
              with another Member's use of the Services; or
            </li>
            <li>
              Otherwise violating applicable law, these Terms, or the Operating
              Policies.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          Access to certain Services may be restricted based on age,
          eligibility, jurisdiction, or other legal requirements.
        </li>
        <li>
          Where a Member violates this Article, the Company may investigate the
          matter and may remove Content, temporarily or permanently restrict
          certain features, suspend the Account, or terminate the service
          agreement.
        </li>
        <li>
          The Company may immediately take stronger measures in cases involving
          serious unlawful conduct, substantial harm to another person, threats
          to safety, security risks, or repeated violations.
        </li>
        <li>
          A Member may contact Customer Support to request review of a
          restriction imposed by the Company.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 12. Termination of the Service Agreement
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          When a Member deletes their Account, the service agreement between the
          Member and the Company is terminated.
        </li>
        <li>
          A Member may request deletion of their Account at any time through the
          functionality provided within the Services or by contacting Customer
          Support.
        </li>
        <li>
          Upon termination, Member information and service data will be handled
          in accordance with applicable law and the{" "}
          <strong>pregnancy&amp;baby Privacy Policy</strong>.
        </li>
        <li>
          Personal Data that identifies the Member will be deleted in accordance
          with the Privacy Policy, except where retention is required or
          permitted by applicable law.
        </li>
        <li>
          Certain service data may be irreversibly anonymized and retained for
          research, statistical, analytical, and service-improvement purposes as
          described in the Privacy Policy.
        </li>
        <li>
          Content that a Member has shared publicly or with other Members,
          including community posts and comments, may not be automatically
          removed when the Account is deleted if such Content forms part of
          other Members' conversations, interactions, or shared areas.
        </li>
        <li>
          Members are encouraged to delete any Content they wish to remove
          before deleting their Accounts. If Content remains after Account
          deletion, identifying Account Information, such as the Member's
          nickname, may be removed or disassociated as appropriate.
        </li>
        <li>
          If a Member uses a third-party login provider such as Google, Apple
          and terminates or disconnects the linked third-party account, access
          to pregnancy&amp;baby through that login method may no longer be
          available.
        </li>
        <li>
          Data deleted upon Account deletion generally cannot be recovered.
        </li>
        <li>
          A former Member may apply to create a new Account unless
          re-registration is restricted due to previous violations of applicable
          law, these Terms, or the Operating Policies.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 13. Protection of Personal Data
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          The Company processes Personal Data as necessary to provide the
          Services and in accordance with applicable privacy and data protection
          laws.
        </li>
        <li>
          Personal Data will be processed only for the purposes and within the
          scope described in the Company's Privacy Policy, applicable consent
          notices, and other legally required disclosures.
        </li>
        <li>
          Where a feature requires the processing of health information or other
          sensitive Personal Data, the Company will obtain the consent required
          by applicable law.
        </li>
        <li>
          The Company will not disclose Personal Data to independent third
          parties except:
          <PolicyHyphenList>
            <li>With the Member's consent;</li>
            <li>As described in the Privacy Policy;</li>
            <li>
              To service providers processing data on the Company's behalf;
            </li>
            <li>Where required or permitted by law; or</li>
            <li>In other circumstances legally permitted.</li>
          </PolicyHyphenList>
        </li>
        <li>
          More detailed information concerning the Company's collection, use,
          sharing, retention, international transfer, and protection of Personal
          Data is provided in the{" "}
          <strong>pregnancy&amp;baby Privacy Policy</strong>.
        </li>
      </PolicyNumberList>

      <PolicySectionTitle>Chapter 4. Paid Services</PolicySectionTitle>
      <PolicyArticleTitle>
        Article 14. Information About Paid Services
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          If the Company offers Paid Services, Members may access such services
          by paying the applicable <strong>one-time fee</strong> in accordance
          with the terms presented for the relevant Paid Service.
        </li>
        <li>
          Before a purchase is completed, the Company or the applicable app
          marketplace will make reasonably available information such as:
          <PolicyHyphenList>
            <li>The name or description of the Paid Service;</li>
            <li>The provider of the Paid Service;</li>
            <li>The content and principal features of the Paid Service;</li>
            <li>The applicable price; and</li>
            <li>
              Other material purchase conditions required by applicable law.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          Availability of Paid Services may depend on the user's device,
          operating system, country, app marketplace, and other technical
          requirements.
        </li>
        <li>
          Unless otherwise expressly stated, Paid Services are{" "}
          <strong>
            one-time purchases and do not automatically renew or result in
            recurring charges
          </strong>
          .
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 15. Formation of a Paid Service Agreement
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          A Member may purchase a Paid Service through the purchase procedure
          made available by the Company or the relevant app marketplace.
        </li>
        <li>
          The purchase process may include:
          <PolicyHyphenList>
            <li>Reviewing and selecting the Paid Service;</li>
            <li>Reviewing the price and material conditions;</li>
            <li>Selecting an available payment method;</li>
            <li>Confirming the purchase; and</li>
            <li>Completing payment.</li>
          </PolicyHyphenList>
        </li>
        <li>
          A Paid Service agreement is formed when the purchase is successfully
          completed and confirmation is displayed or otherwise provided.
        </li>
        <li>
          The Company may refuse, cancel, or suspend a transaction if:
          <PolicyHyphenList>
            <li>False or unauthorized information is used;</li>
            <li>The payment method is not validly authorized;</li>
            <li>
              The Member is not legally permitted to enter into the transaction;
            </li>
            <li>Technical or operational problems prevent completion;</li>
            <li>The Member's Account is suspended or restricted; or</li>
            <li>
              The transaction violates applicable law, these Terms, or
              applicable marketplace policies.
            </li>
          </PolicyHyphenList>
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 16. Payment for Paid Services
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Payment methods available to a Member may vary depending on country,
          device, platform, and the payment methods supported by the applicable
          app marketplace or payment provider.
        </li>
        <li>
          Where a Paid Service is purchased through a mobile application,
          payment may be processed as a{" "}
          <strong>one-time in-app purchase</strong> through services such as:
          <PolicyHyphenList>
            <li>Apple App Store</li>
            <li>Google Play</li>
          </PolicyHyphenList>
        </li>
        <li>
          Payments made through an app marketplace are subject to the
          marketplace's payment terms, billing rules, cancellation procedures,
          refund policies, and other applicable policies.
        </li>
        <li>
          Members must use only payment methods that they are lawfully
          authorized to use.
        </li>
        <li>
          Members are responsible for ensuring that information submitted in
          connection with payment is accurate and authorized.
        </li>
        <li>
          Payment providers or app marketplaces may impose transaction limits,
          regional restrictions, age requirements, or other conditions.
        </li>
        <li>
          The Company does not store full payment-card information where payment
          is processed exclusively by an app marketplace or independent payment
          provider.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 17. Cancellation and Withdrawal from Paid Services
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          A Member's eligibility to cancel a purchase, exercise a right of
          withdrawal, or obtain a refund for a Paid Service will be determined
          by:
          <PolicyHyphenList>
            <li>Applicable consumer protection laws;</li>
            <li>The nature of the relevant Paid Service;</li>
            <li>Whether the Paid Service has already been provided or used;</li>
            <li>The terms disclosed at the time of purchase; and</li>
            <li>
              Where purchased through an app marketplace, the applicable
              policies of that marketplace.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          For purchases made through the Apple App Store or Google Play,
          requests for cancellation, withdrawal, or refund may need to be
          submitted directly through the applicable marketplace in accordance
          with its policies.
        </li>
        <li>
          Certain digital content or services may not be eligible for withdrawal
          or refund after provision or use has begun where permitted by
          applicable law and where any legally required notice or consent has
          been provided.
        </li>
        <li>
          Nothing in these Terms limits any mandatory cancellation, withdrawal,
          or refund rights granted to a Member under applicable law.
        </li>
        <li>
          Benefits, coupons, or other additional benefits provided in connection
          with a refunded purchase may be withdrawn or their use restricted to
          the extent permitted by applicable law.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 18. Restrictions on the Use of Paid Services
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          The Company will make a properly purchased Paid Service available to
          the Member in accordance with the conditions presented for that
          service.
        </li>
        <li>
          The Company may restrict or suspend access to a Paid Service to the
          extent permitted by applicable law where a Member:
          <PolicyHyphenList>
            <li>Violates these Terms;</li>
            <li>Engages in fraudulent or unlawful activity;</li>
            <li>Uses an unauthorized payment method;</li>
            <li>Abuses refund or payment systems; or</li>
            <li>
              Otherwise engages in conduct that provides reasonable grounds for
              restriction under applicable law.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          Where appropriate, a Member may contact Customer Support to request
          review of such a restriction.
        </li>
        <li>
          A restriction on the use of a Paid Service does not, unless otherwise
          expressly stated, terminate the Member's pregnancy&amp;baby Account.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>Article 19. Refunds</PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Refund eligibility and the amount of any refund depend on applicable
          law, the type of Paid Service, whether the service or digital content
          has already been provided or used, and the policies disclosed at the
          time of purchase.
        </li>
        <li>
          Where a purchase was processed through the{" "}
          <strong>Apple App Store or Google Play</strong>, the applicable
          marketplace's refund policies and procedures generally apply, and the
          Company may not be able to issue the refund directly.
        </li>
        <li>
          Certain digital content or services may not be refundable after
          provision or use has begun where permitted by applicable law and where
          any legally required notice or consent has been provided.
        </li>
        <li>
          If a separate refund policy applies to a particular Paid Service, the
          Company will make that policy available during the purchase process.
        </li>
        <li>
          Nothing in this Article excludes or limits any refund, cancellation,
          withdrawal, or other consumer rights that cannot lawfully be excluded
          or limited under the laws applicable to the Member.
        </li>
      </PolicyNumberList>

      <PolicySectionTitle>Chapter 5. Other Provisions</PolicySectionTitle>
      <PolicyArticleTitle>
        Article 20. Disclaimer and Liability
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          To the maximum extent permitted by applicable law, the Company does
          not make warranties regarding matters that are not expressly set forth
          in these Terms.
        </li>
        <li>
          The Company does not guarantee the completeness, accuracy,
          reliability, or suitability of information provided by:
          <PolicyHyphenList>
            <li>Members;</li>
            <li>Third-party content providers;</li>
            <li>Public databases;</li>
            <li>External services; or</li>
            <li>Other independent sources.</li>
          </PolicyHyphenList>
        </li>
        <li>
          The Services, including menstrual cycle predictions, fertility
          estimates, pregnancy-related calculations, fetal growth analysis,
          child growth and developmental information, and other health-related
          features, are provided for{" "}
          <strong>informational and supportive purposes</strong>.
        </li>
        <li>
          The Services do not constitute and are not intended to replace
          professional medical diagnosis, examination, treatment, or advice.
        </li>
        <li>
          Where the Company is responsible for loss caused by its intentional
          misconduct or negligence, the Company will be liable in accordance
          with applicable law.
        </li>
        <li>
          To the extent permitted by applicable law, the Company will not be
          responsible for losses not caused by the Company's intentional
          misconduct or negligence, including losses arising solely from:
          <PolicyHyphenList>
            <li>
              Force majeure or circumstances beyond the Company's reasonable
              control;
            </li>
            <li>The Member's own acts or omissions;</li>
            <li>
              Unauthorized access or attacks by third parties that could not
              reasonably have been prevented;
            </li>
            <li>Malware or malicious software introduced by third parties;</li>
            <li>
              Failures of third-party networks, systems, or services outside the
              Company's reasonable control; or
            </li>
            <li>
              Other circumstances not caused by the Company's intentional
              misconduct or negligence.
            </li>
          </PolicyHyphenList>
        </li>
        <li>
          To the extent permitted by applicable law, the Company is not
          responsible for disputes arising solely between Members or between a
          Member and an independent third party, unless the Company is legally
          responsible for the matter.
        </li>
        <li>
          Nothing in these Terms excludes or limits liability that cannot
          lawfully be excluded or limited under applicable consumer protection
          or other mandatory laws.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 21. Notices and Communications
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Members may contact the Company through Customer Support at any time
          regarding the Services.
        </li>
        <li>
          General notices to Members may be provided through in-app notices for
          at least seven (7) days or by another reasonable method.
        </li>
        <li>
          Matters that materially affect a Member's rights or use of the
          Services may be communicated individually through:
          <PolicyHyphenList>
            <li>Email;</li>
            <li>Push notification;</li>
            <li>In-app message; or</li>
            <li>Other appropriate electronic means.</li>
          </PolicyHyphenList>
        </li>
        <li>
          The Company is not required to use a communication method for which a
          Member has not provided the information necessary to receive such
          communication.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>
        Article 22. Governing Law and Dispute Resolution
      </PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          These Terms and the relationship between the Company and Members are
          generally governed by the laws of the{" "}
          <strong>Republic of Korea</strong>, without prejudice to mandatory
          consumer protection provisions or other mandatory rights applicable
          under the laws of the Member's country or region of residence.
        </li>
        <li>
          If a dispute arises in connection with the Services, the Company and
          the Member will first make reasonable efforts to resolve the matter
          through good-faith consultation.
        </li>
        <li>
          If the dispute cannot be resolved amicably, it may be submitted to a
          court having jurisdiction under applicable law.
        </li>
        <li>
          Nothing in these Terms deprives a Member of the right to bring a claim
          before a court or competent authority where such right is guaranteed
          by mandatory law in the Member's country or region of residence.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>Article 23. Miscellaneous</PolicyArticleTitle>
      <PolicyNumberList>
        <li>
          Matters not expressly provided for in these Terms will be governed by
          applicable law and, where permitted, generally accepted practices.
        </li>
        <li>
          If any provision of these Terms is held invalid, illegal, or
          unenforceable, the remaining provisions will remain in effect to the
          fullest extent permitted by law.
        </li>
        <li>
          The Company's failure to enforce any provision of these Terms does not
          constitute a waiver of that provision or any other right.
        </li>
        <li>
          The headings used in these Terms are for convenience only and do not
          affect their interpretation.
        </li>
      </PolicyNumberList>

      <PolicyArticleTitle>Supplementary Provision</PolicyArticleTitle>
      <PolicyText>
        These Terms take effect on <strong>[September 21, 2026]</strong>.
      </PolicyText>
      <PolicyText>
        For Members who register after these Terms are posted but before the
        stated Effective Date, the Company may apply these Terms from the date
        of registration where permitted by applicable law.
      </PolicyText>
    </PolicyContainer>
  );
};

export default ServiceEn;
